import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button.tsx";
import { useModal } from "@/components/core/modals/use-modal.ts";
import { ScrollArea } from "@/components/core/scroller";
import {
	IconChevronDown,
	IconDots,
	IconSearch,
	IconX,
} from "@tabler/icons-react";
import clsx from "clsx";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

export type ColumnDef<T> = {
	id: keyof T | string;
	label?: string | React.ReactNode;
	headerClassName?: string;
	cellClassName?: string;
	cell?: (props: {
		data: T;
		onClick?: (data: T) => void;
		onUpdate?: (updatedValue: Partial<T>) => void;
	}) => ReactNode;
	renderCondition?: (data: T) => boolean;
};

export type DataInputTableProps<T> = {
	data?: T[];
	columns: ColumnDef<T>[];
	className?: string;
	onEdit?: (data: T) => void;
	onRowClick?: (data: T) => void;
	onDelete?: (data: T) => void;
	showToolbar?: boolean;
};

function TableSearch({
	value = "",
	onChange,
	onClear,
}: {
	value?: string;
	onChange: (value: string) => void;
	onClear?: () => void;
}) {
	return (
		<div className="flex flex-row items-center w-full gap-1">
			<div className="flex flex-row items-center w-full h-[38px] border border-border rounded-md overflow-hidden">
				<div className="inline-flex justify-center items-center h-[38px] w-[38px] shrink-0 text-foreground opacity-50">
					<IconSearch size={18} stroke={1.5} />
				</div>
				<input
					placeholder="Looking for something?"
					className="flex w-full h-full border-0 outline-none focus:ring-0 p-0 text-base text-foreground"
					value={value}
					autoComplete="off"
					onChange={(e) => onChange(e.target.value)}
				/>
				{value && (
					<button
						onClick={onClear}
						type="button"
						className="inline-flex justify-center items-center h-[38px] w-[38px] shrink-0 text-foreground opacity-50 hover:opacity-100"
					>
						<IconX size={18} stroke={1.5} />
					</button>
				)}
			</div>
		</div>
	);
}

export default function DataInputTable<T>({
	data,
	columns,
	className,
	onEdit,
	onRowClick,
	onDelete,
	showToolbar = false,
}: DataInputTableProps<T>) {
	const modal = useModal();

	const [searchTerm, setSearchTerm] = useState("");
	const [visibleColumns, setVisibleColumns] = useState<string[]>(
		columns.map((col) => col.id as string),
	);

	const toggleColumn = (columnId: string) => {
		setVisibleColumns((prev) =>
			prev.includes(columnId)
				? prev.filter((id) => id !== columnId)
				: [...prev, columnId],
		);
	};

	const filteredData = data?.filter((item) =>
		Object.values(item).some((val) =>
			String(val).toLowerCase().includes(searchTerm.toLowerCase()),
		),
	);

	return (
		<div className="flex flex-col w-full gap-4">
			{showToolbar && (
				<div className="flex flex-row w-full gap-2">
					<TableSearch
						value={searchTerm}
						onChange={setSearchTerm}
						onClear={() => setSearchTerm("")}
					/>
					<DropdownMenu modal={false}>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost">
								Columns <IconChevronDown size={16} stroke={2} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							{columns.map((column) => (
								<DropdownMenuCheckboxItem
									key={column.id as string}
									checked={visibleColumns.includes(column.id as string)}
									onCheckedChange={() => toggleColumn(column.id as string)}
								>
									{column.label}
								</DropdownMenuCheckboxItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			)}
			<ScrollArea className={clsx("flex flex-col w-full", className)}>
				<table className="w-full">
					<thead>
						<tr>
							{columns
								.filter((col) => visibleColumns.includes(col.id as string))
								.map((column, index) => (
									<th
										key={index}
										className={clsx("text-left p-3", column.headerClassName)}
									>
										{column.label}
									</th>
								))}
						</tr>
					</thead>
					<tbody>
						{filteredData?.map((row, rowIndex) => (
							<tr key={rowIndex}>
								{columns
									.filter((col) => visibleColumns.includes(col.id as string))
									.map((column, colIndex) => (
										<td
											key={colIndex}
											className={clsx("p-3", column.cellClassName)}
										>
											{column.cell?.({ data: row, onClick: onRowClick })}
										</td>
									))}
							</tr>
						))}
					</tbody>
				</table>
			</ScrollArea>
		</div>
	);
}
